import type { ContentPagePageItems } from '#root/api/clients/content/data-contracts'
import type { RouteLocationNormalizedLoaded } from 'vue-router'

interface GenericEspotSection {
  name: string
  items?: ContentPagePageItems
  lazy?: boolean
}

/**
 * Mutates a CMS page response in-place to merge "inherited" eSpot sections into their owning
 * sections. For each section whose name ends in `-inherited`, the inherited items are either
 * prepended (when the section is listed under a matching `inheritedFirstEspotsOnRoutes` pattern)
 * or appended to the existing items. Empty sections drop to `null` outside preview mode.
 * Builds a `sectionsMap` for O(1) lookup and replaces `content.sections` with its values. No-op
 * when `sectionsMap` already exists (avoids re-running on hydrated SSR payloads).
 * @param response - The CMS response (raw or `useAsyncData` ref-shaped).
 * @param route - Active Vue Router route - used to test `inheritedFirstEspotsOnRoutes` patterns.
 * @param isPreview - Whether the page is rendered in CMS preview mode (keeps empty sections).
 * @param inheritedFirstEspotsOnRoutes - Per-route-pattern lists of eSpot names whose inherited
 *                                       items should be prepended rather than appended.
 * @category Utils
 */
export const mergeInheritedEspotSections = (
  response: any,
  route: RouteLocationNormalizedLoaded,
  isPreview: boolean,
  inheritedFirstEspotsOnRoutes: { pattern: string, espots: string[] }[]
) => {
  const contentSource = response.value || response
  // Since this gets transformed on SSR, we ignore if the transformation if the sectionMap is already on the response, preventing transforming twice
  if (contentSource?.content?.sections && !contentSource?.content?.sectionsMap) {
    const mergedSections = contentSource.content.sections.reduce((acc, { name: originalName, items = [] }) => {
      const inheritedFirstEspots = inheritedFirstEspotsOnRoutes
        .filter(({ pattern }) => convertPatternToRegex(pattern).test(route.path))
        .map(({ espots }) => espots)
        .flat()

      const name = originalName.replace('-inherited', '')

      if (items.length === 0) {
        if (isPreview)
          return { ...acc, [name]: acc[name] || { name, items: [] } }
        else
          return acc
      }

      const existingItems = acc[name]?.items || []

      return {
        ...acc,
        [name]: {
          name,
          lazy: name.includes('lazy'),
          items: inheritedFirstEspots.includes(name) && originalName.includes('-inherited')
            ? [...items, ...existingItems]
            : [...existingItems, ...items],
        },
      }
    }, {} as Record<string, GenericEspotSection>)

    contentSource.content.sectionsMap = mergedSections
    contentSource.content.sections = Object.values(mergedSections)
  }
}
